import { useState, useRef, useContext } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, Send, CheckCircle, Upload, FileText, AlertCircle, Phone } from "lucide-react";
import axios from "axios";
import TermsModal from "./TermsModal";
import { PriceContext } from "@/PriceContext";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios with retry logic
const axiosInstance = axios.create({
	timeout: 60000, // 60 seconds
	headers: {
		'Content-Type': 'application/json',
	},
});

// Retry logic
axiosInstance.interceptors.response.use(
	response => response,
	async error => {
		const config = error.config;
		if (!config || !config.retry) {	config.retry = 0; }

		if (config.retry < 3 && error.code === 'ECONNABORTED') {
			config.retry += 1;
			await new Promise(resolve => setTimeout(resolve, 1000 * config.retry));
			return axiosInstance(config);
		}
		return Promise.reject(error);
	}
);

export default function AgreementFormNew({ selectedOption, onBack }) {
	const prices = useContext(PriceContext);
	const [submitted, setSubmitted] = useState(false);
	const [loading, setLoading] = useState(false);
	const [showTermsModal, setShowTermsModal] = useState(false);
	const [error, setError] = useState("");

	// File upload refs
	const passportVisaRef = useRef(null);
	const driverLicenseRef = useRef(null);
	const otherDocRef = useRef(null);
  
	const [formData, setFormData] = useState({
		// Customer Details
		name: "",
		address: "",
		mobileNumber: "",
		email: "",

		// Bike Details
		bikeNumber: "",
		releaseDate: new Date().toISOString().split('T')[0],
		rentalBond: "200",
		rentalWeek: "0",
		rentToOwn: false,

		// Document Uploads (minimum 2 required)
		passportVisaDocument: null,
		driverLicenseDocument: null,
		otherDocument: null,

		// Agreement
		agreedToTerms: false
	});
  
	const formType = formData.rentToOwn ? "Rent To Own" : "Rental";

	const [uploadStatus, setUploadStatus] = useState({
		passportVisa: "",
		driverLicense: "",
		other: ""
	});

	const handleInputChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
		setError(""); // Clear error when user types
	};

	const handleFileUpload = async (field, file, statusKey) => {
		if (!file) return;

		setError("");

		// Check file size (20MB = 20 * 1024 * 1024 bytes)
		const maxSize = 20 * 1024 * 1024;
		if (file.size > maxSize) {
			setError(`${statusKey} file must be less than 20MB!`);
			setUploadStatus(prev => ({ ...prev, [statusKey]: "error" }));
			return;
		}

		// Set uploading state
		setUploadStatus(prev => ({ ...prev, [statusKey]: "uploading" }));

		// Convert file to base64
		const reader = new FileReader();
		reader.onloadend = () => {
			setFormData(prev => ({
				...prev,
				[field]: {
					name: file.name,
					type: file.type,
					size: file.size,
					data: reader.result.split(",")[1] // ✅ strip "data:...;base64,"
				}
			}));
			setUploadStatus(prev => ({ ...prev, [statusKey]: "success" }));
		};

		reader.onerror = () => {
			setError(`Failed to read ${statusKey} file. Please try again.`);
			setUploadStatus(prev => ({ ...prev, [statusKey]: "error" }));
		};

		reader.readAsDataURL(file);
	};

	const validateForm = () => {
		if (!formData.name.trim()) {
			setError("Please enter your name");
			return false;
		}
	  
		if (!formData.address.trim()) {
			setError("Please enter your address");
			return false;
		}
	  
		if (!formData.mobileNumber.trim()) {
			setError("Please enter your mobile number");
			return false;
		}
	  
		if (!formData.email.trim()) {
			setError("Please enter your email");
			return false;
		}

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(formData.email)) {
			setError("Please enter a valid email address");
			return false;
		}

		const bikeNumberRegex = /^SYD 2\d{3}$/;
		if (!bikeNumberRegex.test(formData.bikeNumber)) {
			setError("You need to enter a valid E-Bike Number. You need to call us for this information !!!");
			return false;
		}

		if (!formData.rentalWeek || Number(formData.rentalWeek) <= 0) {
			setError("Please enter a valid number of rental weeks");
			return false;
		}

		const uploadedDocs = [
			formData.passportVisaDocument,
			formData.driverLicenseDocument,
			formData.otherDocument
		].filter(doc => doc !== null);

		if (uploadedDocs.length < 2) {
			setError("Please upload at least 2 documents (Passport/Visa, Driver License, or Other)");
			return false;
		}

		if (!formData.agreedToTerms) {
			setError("You must agree to the Terms and Conditions");
			return false;
		}

		return true;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm()) return;

		setLoading(true);
		setError("");

		try {
			const submissionData = {
				...formData,
				optionType: selectedOption.type,
				batteryType: selectedOption.battery,
			};

			const response = await axiosInstance.post(
				`${BACKEND_URL}/api/submit-agreement`,
				submissionData,
				{ headers: { "Content-Type": "application/json" } }
			);

			const data = response?.data;
			console.log("Agreement submit response:", data);

			if (response.status === 200 && data?.success) {
				setSubmitted(true);

				const redirectUrl = data.redirect;

				if (redirectUrl) {
					window.location.href = redirectUrl;
				} else {
					setError("Agreement submitted but no payment redirect provided.");
				}

			} else {
				setError(data?.message || "Failed to submit agreement. Please try again.");
			}

		} catch (err) {
			console.error("Error submitting agreement:", err);

			let errorMessage = "Failed to submit agreement. ";

			if (err.code === "ECONNABORTED") {
				errorMessage += "Request timed out. Please check your internet connection and try again.";
			} else if (err.response) {
				errorMessage += err.response.data?.detail || err.response.statusText || "Server error.";
			} else if (err.request) {
				errorMessage += "No response from server. Please check your connection.";
			} else {
				errorMessage += err.message || "Unknown error occurred.";
			}

			setError(errorMessage);

		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-4 sm:py-12 px-2 sm:px-4">
			<div className="max-w-4xl mx-auto">
				<Card className="shadow-2xl border-t-4 border-[#FF8C42]">
					<CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3748] text-white p-4 sm:p-6">
						<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
							<Button
								onClick={onBack}
								variant="outline"
								className="border-white text-white hover:bg-white/10 text-sm"
							>
								<ArrowLeft className="mr-2 h-4 w-4" />
								Back
							</Button>

							<div className="text-left sm:text-center flex-1">
								<CardTitle className="text-xl sm:text-3xl">
									{formType} Agreement Form
								</CardTitle>
								<CardDescription className="text-gray-300 text-sm sm:text-lg mt-1 sm:mt-2">
									{selectedOption.battery}
								</CardDescription>
							</div>
						</div>
					</CardHeader>

					<CardContent className="p-4 sm:p-8">
						<form onSubmit={handleSubmit} className="space-y-6">
							<div className="bg-red-50 border-l-4 border-red-500 p-4 rounded flex items-start">
								<p className="text-red-700 text-sm">
									<strong>!!! IMPORTANT !!!</strong> <br /><br />
									Please call us first <br />
									You’ll need information from us to complete this form. <br /><br />
									<div className="flex items-start"> 
										<Phone className="mr-2 h-5 w-5" /> 
										<span>Call: 0420 993 083</span> 
									</div>
									<div className="flex items-start"> 
										<Phone className="mr-2 h-5 w-5" /> 
										<span>Call: 0417 232 258</span> 
									</div>
								</p>
							</div>

							{/* Error Message */}
							{error && (
								<div className="bg-red-50 border-l-4 border-red-500 p-4 rounded flex items-start">
									<AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
									<p className="text-red-700 text-sm">{error}</p>
								</div>
							)}

							{/* Customer Details */}
							<div className="space-y-4">
								<h3 className="text-xl font-semibold text-gray-800 border-b-2 border-[#FF8C42] pb-2">
									Customer Details
								</h3>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<Label htmlFor="name">Full Name *</Label>
										<Input
											id="name"
											placeholder="Enter your full name"
											value={formData.name}
											onChange={(e) => handleInputChange("name", e.target.value)}
											required
										/>
									</div>

									<div>
										<Label htmlFor="email">Email *</Label>
										<Input
											id="email"
											type="email"
											placeholder="your.email@example.com"
											value={formData.email}
											onChange={(e) => handleInputChange("email", e.target.value)}
											required
										/>
									</div>

									<div>
										<Label htmlFor="address">Address *</Label>
										<Input
											id="address"
											placeholder="Your full address"
											value={formData.address}
											onChange={(e) => handleInputChange("address", e.target.value)}
											required
										/>
									</div>

									<div>
										<Label htmlFor="mobile">Mobile Number *</Label>
										<Input
											id="mobile"
											placeholder="04XX XXX XXX"
											value={formData.mobileNumber}
											onChange={(e) => handleInputChange("mobileNumber", e.target.value)}
											required
										/>
									</div>
								</div>
							</div>

							{/* Bike & Rental Details */}
							<div className="space-y-4">
								<h3 className="text-xl font-semibold text-gray-800 border-b-2 border-[#FF8C42] pb-2">
									Bike & Rental Details
								</h3>

								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									<div>
										<Label htmlFor="bikeNumber">E-Bike Number *</Label>
										<Input
											id="bikeNumber"
											placeholder="Please call us for this information"
											value={formData.bikeNumber}
											onChange={(e) => handleInputChange("bikeNumber", e.target.value)}
											required
										/>
									</div>

									<div>
										<Label htmlFor="releaseDate">Date of Rent Starts *</Label>
										<Input
											id="releaseDate"
											type="date"
											value={formData.releaseDate}
											onChange={(e) => handleInputChange("releaseDate", e.target.value)}
											required
										/>
									</div>

									<div>
										<Label htmlFor="rentalBond">Rental Bond *</Label>
										<Input
											id="rentalBond"
											type="number"
											value={formData.rentalBond}
											onChange={(e) => handleInputChange("rentalBond", e.target.value)}
											required
										/>
									</div>

									<div>
										<Label htmlFor="rentalWeek">Number of Rental Weeks *</Label>
										<Input
											id="rentalWeek"
											type="number"
											value={formData.rentalWeek}
											onChange={(e) => handleInputChange("rentalWeek", e.target.value)}
											required
										/>
									</div>

									<div>
										<Label>Rent To Own</Label>
										<div className="flex items-center gap-4 mt-2">
											<label className="flex items-center gap-2">
												<input
													type="radio"
													name="rentToOwn"
													value="yes"
													checked={formData.rentToOwn === true}
													onChange={() => handleInputChange("rentToOwn", true)}
												/>
												Yes
											</label>

											<label className="flex items-center gap-2">
												<input
													type="radio"
													name="rentToOwn"
													value="no"
													checked={formData.rentToOwn === false}
													onChange={() => handleInputChange("rentToOwn", false)}
												/>
												No
											</label>
										</div>
									</div>
								</div>
							</div>

							{/* Document Uploads */}
							<div className="space-y-4">
								<h3 className="text-xl font-semibold text-gray-800 border-b-2 border-[#FF8C42] pb-2">
									Document Uploads
								</h3>

								{/* Passport/Visa Upload */}
								<div className="border rounded-lg p-4">
									<Label htmlFor="passportVisa" className="text-base font-semibold">
										Passport/Visa
									</Label>

									<div className="mt-2 flex items-center gap-4">
										<input
											ref={passportVisaRef}
											type="file"
											id="passportVisa"
											accept="image/*,.pdf"
											onChange={(e) =>
												handleFileUpload("passportVisaDocument", e.target.files[0], "passportVisa")
											}
											className="hidden"
										/>

										<Button
											type="button"
											onClick={() => passportVisaRef.current?.click()}
											variant="outline"
											className="flex items-center"
											disabled={uploadStatus.passportVisa === "uploading"}
										>
											<Upload className="mr-2 h-4 w-4" />
											{uploadStatus.passportVisa === "uploading" ? "Uploading..." : "Choose File"}
										</Button>

										{uploadStatus.passportVisa === "success" &&
											formData.passportVisaDocument && (
												<div className="flex items-center text-green-600">
													<CheckCircle className="h-5 w-5 mr-2" />
													<span className="text-sm">
														{formData.passportVisaDocument.name}
													</span>
												</div>
											)}
									</div>
								</div>

								{/* Driver License Upload */}
								<div className="border rounded-lg p-4">
									<Label htmlFor="driverLicense" className="text-base font-semibold">
										Driver License
									</Label>

									<div className="mt-2 flex items-center gap-4">
										<input
											ref={driverLicenseRef}
											type="file"
											id="driverLicense"
											accept="image/*,.pdf"
											onChange={(e) => handleFileUpload("driverLicenseDocument", e.target.files[0], "driverLicense")}
											className="hidden"
										/>

										<Button
											type="button"
											onClick={() => driverLicenseRef.current?.click()}
											variant="outline"
											className="flex items-center"
											disabled={uploadStatus.driverLicense === "uploading"}
										>
											<Upload className="mr-2 h-4 w-4" />
											{uploadStatus.driverLicense === "uploading" ? "Uploading..." : "Choose File"}
										</Button>

										{uploadStatus.driverLicense === "success" &&
											formData.driverLicenseDocument && (
												<div className="flex items-center text-green-600">
													<CheckCircle className="h-5 w-5 mr-2" />
													<span className="text-sm">
														{formData.driverLicenseDocument.name}
													</span>
												</div>
											)}
									</div>
								</div>

								{/* Other Document Upload */}
								<div className="border rounded-lg p-4">
									<Label htmlFor="otherDoc" className="text-base font-semibold">
										Other ID/Document
									</Label>

									<div className="mt-2 flex items-center gap-4">
										<input
											ref={otherDocRef}
											type="file"
											id="otherDoc"
											accept="image/*,.pdf"
											onChange={(e) =>
												handleFileUpload("otherDocument", e.target.files[0], "other")
											}
											className="hidden"
										/>

										<Button
											type="button"
											onClick={() => otherDocRef.current?.click()}
											variant="outline"
											className="flex items-center"
											disabled={uploadStatus.other === "uploading"}
										>
											<Upload className="mr-2 h-4 w-4" />
											{uploadStatus.other === "uploading" ? "Uploading..." : "Choose File"}
										</Button>

										{uploadStatus.other === "success" &&
											formData.otherDocument && (
												<div className="flex items-center text-green-600">
													<CheckCircle className="h-5 w-5 mr-2" />
													<span className="text-sm">
														{formData.otherDocument.name}
													</span>
												</div>
											)}
									</div>
								</div>
							</div>

							{/* Terms & Conditions */}
							<div className="space-y-4">
								<h3 className="text-xl font-semibold text-gray-800 border-b-2 border-[#FF8C42] pb-2">
									Terms & Conditions
								</h3>

								<div className="bg-gray-50 p-4 rounded-lg">
									<button
										type="button"
										onClick={() => setShowTermsModal(true)}
										className="flex items-center text-[#FF8C42] hover:text-[#e67a32] font-semibold"
									>
										<FileText className="mr-2 h-5 w-5" />
										Click here to read the full Terms & Conditions
									</button>

									<div className="flex items-start mt-4">
										<Checkbox
											id="agreedToTerms"
											checked={formData.agreedToTerms}
											onCheckedChange={(checked) =>
												handleInputChange("agreedToTerms", checked)
											}
											className="mt-1"
										/>

										<Label
											htmlFor="agreedToTerms"
											className="ml-3 text-sm cursor-pointer"
										>
											<strong>I have read and agree to the Terms & Conditions</strong>{" "}
											of this rental agreement. I understand my responsibilities and
											liabilities as outlined in the agreement.
										</Label>
									</div>
								</div>
							</div>

							{/* Submit Button */}
							<div className="flex justify-center pt-6">
								<Button
									type="submit"
									disabled={loading}
									className="w-full sm:w-auto bg-gradient-to-r from-[#FF8C42] to-[#ff7b29] hover:from-[#e67a32] hover:to-[#e66a1a] text-white font-bold py-3 px-6 sm:px-8 text-base sm:text-lg"
								>
									{loading ? (
										<>Submitting...</>
									) : (
										<>
											<Send className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
											Submit Agreement & Pay
										</>
									)}
								</Button>
							</div>
						</form>
					</CardContent>
				</Card>
			</div>

			{/* Terms Modal */}
			<TermsModal
				isOpen={showTermsModal}
				onClose={() => setShowTermsModal(false)}
				battery={selectedOption.battery}
			/>
		</div>
	);
}
